import { createAsyncThunk } from '@reduxjs/toolkit';
import { AccountRepositoryImpl } from '../Data/repositories/AccountRepositoryImpl';
import { CreateAccountUseCase } from '../Domain/UseCase/CreateAccountUseCase';
import { GetAccountsUseCase } from '../Domain/UseCase/GetAccountsUseCase';
import { UpdateAccountUseCase } from '../Domain/UseCase/UpdateAccountUseCase';
import { DeleteAccountUseCase } from '../Domain/UseCase/DeleteAccountUseCase';
import { ResetPasswordUseCase } from '../Domain/UseCase/ResetPasswordUseCase';
import { GetSlackNotifySettingsUseCase } from '../Domain/UseCase/GetSlackNotifySettingsUseCase';
import { UpdateSlackNotifySettingsUseCase } from '../Domain/UseCase/UpdateSlackNotifySettingsUseCase';
import { CreateAccountDTO } from '../Domain/Repositories/AccountRepository';
import { DeleteAccountsUseCase } from '../Domain/UseCase/DeleteAccountsUseCase';
import { isAxiosError } from 'axios';
import { User } from '../../User/Model/User';
import { SlackNotifySettingsPatch } from '../Model/SlackNotifySettings';

const repo = new AccountRepositoryImpl();

export const fetchAccounts = createAsyncThunk('account/fetchAll', () => {
  return new GetAccountsUseCase(repo).execute();
});

export const addAccount = createAsyncThunk(
  'account/create',
  async (payload: CreateAccountDTO, { rejectWithValue }) => {
    try {
      return await new CreateAccountUseCase(repo).execute(payload);
    } catch (err) {
      if (isAxiosError(err) && err.response?.data) return rejectWithValue(err.response.data);
      throw err;
    }
  },
);

export const editAccount = createAsyncThunk(
  'account/update',
  (args: { id: number; dto: Partial<CreateAccountDTO>; me: User | null }) => {
    return new UpdateAccountUseCase(repo).execute(args.id, args.dto, args.me ?? null);
  },
);

export const removeAccount = createAsyncThunk('account/delete', (id: number) => {
  return new DeleteAccountUseCase(repo).execute(id);
});

export const removeAccountMany = createAsyncThunk(
  'account/deleteMany',
  (ids: number[]) => new DeleteAccountsUseCase(repo).execute(ids),
);

export const resetPassword = createAsyncThunk(
  'account/resetPassword',
  async (args: { id: number; newPassword: string }, { rejectWithValue }) => {
    try {
      await new ResetPasswordUseCase(repo).execute(args.id, args.newPassword);
      return args.id;
    } catch (err) {
      if (isAxiosError(err) && err.response?.data) return rejectWithValue(err.response.data);
      throw err;
    }
  },
);

export const fetchSlackNotifySettings = createAsyncThunk(
  'account/fetchSlackNotifySettings',
  async (_, { rejectWithValue }) => {
    try {
      return await new GetSlackNotifySettingsUseCase(repo).execute();
    } catch (err) {
      if (isAxiosError(err)) {
        return rejectWithValue({ status: err.response?.status });
      }
      throw err;
    }
  },
);

export const updateSlackNotifySettings = createAsyncThunk(
  'account/updateSlackNotifySettings',
  async (dto: SlackNotifySettingsPatch, { rejectWithValue }) => {
    try {
      return await new UpdateSlackNotifySettingsUseCase(repo).execute(dto);
    } catch (err) {
      if (isAxiosError(err)) {
        return rejectWithValue({ status: err.response?.status, data: err.response?.data });
      }
      throw err;
    }
  },
);